import React from 'react';
import { Container} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faBullseye, faRobot, faSitemap, faLightbulb,
    faLayerGroup, faMagnifyingGlass, faArrowDown
} from '@fortawesome/free-solid-svg-icons';

const stages = [
    {
        side: 'left',
        stage: 'THE GOAL',
        icon: faBullseye,
        accentClass: 'accent-secondary',
        instinct: 'What you want',
        title: 'AI that reasons over your business.',
        beats: [
            {kind: 'step', text: 'You want AI to answer the decisions that matter - suitability, compliance, clinical, risk.'},
            {kind: 'step', text: 'Every answer has to be defensible. Cited. Reproducible.'},
            {kind: 'punch', text: 'And your business keeps moving. So the system has to keep up.'}
        ]
    },
    {
        side: 'right',
        stage: 'ONE END OF THE SPECTRUM',
        icon: faRobot,
        accentClass: 'accent-primary-dark',
        instinct: 'Just throw it at an LLM',
        title: 'Fast to demo. Hard to trust.',
        beats: [
            {kind: 'step', text: 'You upload the regulation. You ask questions. Answers come back fast and fluent.'},
            {kind: 'step', text: 'Compliance spots a clause that isn\'t in the regulation. Made up. Citation invented.'},
            {kind: 'step', text: 'Ask the same question tomorrow - different answer. No trace of what drove either one.'},
            {kind: 'punch', text: 'LLMs are built for fluency, not for evidence. They\'ll never survive an audit on their own.'}
        ]
    },
    {
        side: 'left',
        stage: 'THE OTHER END',
        icon: faSitemap,
        accentClass: 'accent-primary',
        instinct: 'Model the whole business first',
        title: 'Rigorous. Slow. Always one step behind.',
        beats: [
            {kind: 'step', text: 'You go properly. Map the business upfront - concepts, classes, relationships, every corner defined before any agent runs.'},
            {kind: 'step', text: 'The first domain comes together. Add a second and nothing fits the categories you just agreed.'},
            {kind: 'step', text: 'Every new view triggers a remodel. Workshops multiply. The use case waits.'},
            {kind: 'punch', text: 'Classical modelling was built for stable libraries - not for a business that changes faster than the model can keep up.'}
        ]
    },
    {
        side: 'right',
        stage: 'THE EPIPHANY',
        icon: faLightbulb,
        accentClass: 'accent-accent',
        instinct: 'The middle ground that actually works',
        title: 'Compliance isn\'t sales. Clinical isn\'t finance.',
        beats: [
            {kind: 'step', text: 'Each part of your business has its own language. Its own processes. Its own way of working.'},
            {kind: 'step', text: 'A customer in sales is a different thing from a customer in compliance - same person, different entity, different rules.'},
            {kind: 'step', text: 'You don\'t need one giant model of everything. And you don\'t need a chatbot guessing at the answer.'},
            {kind: 'punch', text: 'You need each domain captured as its own knowledge base - and agents that can reason inside one or across many.'}
        ]
    },
    {
        side: 'left',
        stage: 'THE ARCHITECTURE',
        icon: faLayerGroup,
        accentClass: 'accent-primary',
        instinct: 'How Nebulyx is different',
        title: 'One knowledge base per domain. Versioned. Cited. Linked at the seams.',
        beats: [
            {kind: 'step', text: 'Every domain - regulation, policy, process, clinical guideline - gets its own scoped knowledge base, captured from the source documents in days, not years.'},
            {kind: 'step', text: 'Each one tracks its own versions. Amendments, overrides, reinterpretations all linked natively as the business moves.'},
            {kind: 'step', text: 'Agents reason inside a domain when it\'s local. Across domains when they need to - through entity resolution at the seams, not one giant model.'},
            {kind: 'punch', text: 'A regulation changes? Just that knowledge base updates. The rest of the business carries on uninterrupted.'}
        ]
    },
    {
        side: 'right',
        stage: 'WHY IT MATTERS',
        icon: faMagnifyingGlass,
        accentClass: 'accent-secondary',
        instinct: 'The auditor\'s question',
        title: '"Why did you decide that, in March?"',
        beats: [
            {kind: 'step', text: 'Six months in. Rule\'s been amended twice. The auditor wants the trail.'},
            {kind: 'step', text: 'An LLM can\'t show one. A big upfront model has remodelled past it.'},
            {kind: 'step', text: 'A per-domain, versioned knowledge base puts the answer one query away - cited to document, page, paragraph.'},
            {kind: 'punch', text: 'Auditor gets their answer. Your team carries on.'}
        ]
    }
];

const Beat = ({beat, isLast}) => (
    <div className={`journey-beat journey-beat-${beat.kind}`}>
        <div className="journey-beat-text">{beat.text}</div>
        {!isLast && (
            <div className="journey-beat-arrow">
                <FontAwesomeIcon icon={faArrowDown}/>
            </div>
        )}
    </div>
);

const JourneySection = () => (
    <section id="the-journey" className="journey-section">
        <Container>
            <div className="text-center mb-5">
                <div className="section-label">The Journey</div>
                <h2>AI reasoning you can trust.</h2>
                <p className="section-intro">
                    Most regulated businesses end up at one of two extremes - a chatbot
                    that can't be trusted, or a modelling programme that can't ship.
                    Here's what each one looks like up close, and the middle ground that
                    actually works.
                </p>
            </div>

            <div className="journey-arc">
                <div className="journey-spine"/>
                {stages.map((s, i) => (
                    <div key={i} className={`journey-stage journey-stage-${s.side} ${s.accentClass}`}>
                        <div className="journey-node">
                            <FontAwesomeIcon icon={s.icon}/>
                        </div>
                        <div className="journey-panel">
                            <div className="journey-stage-label">
                                <span className="journey-stage-num">{s.stage}</span>
                                <span className="journey-stage-sep">/</span>
                                <span className="journey-stage-instinct">{s.instinct}</span>
                            </div>
                            <h3 className="journey-stage-title">{s.title}</h3>
                            <div className="journey-stage-body">
                                {s.beats.map((beat, idx) => (
                                    <Beat
                                        key={idx}
                                        beat={beat}
                                        isLast={idx === s.beats.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="journey-cta-row">
                <p className="journey-cta-text">
                    What this looks like in practice - <strong>Doctor Dog</strong>,
                    a clinical decision in 50 milliseconds, every step cited.
                </p>
                {/*<Button*/}
                {/*    variant="outline-primary"*/}
                {/*    size="lg"*/}
                {/*    href="#doctor-dog-scenario">*/}
                {/*    See it in action <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>*/}
                {/*</Button>*/}
            </div>
        </Container>
    </section>
);

export default JourneySection;